import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { CheckCircle2, Circle, Package, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { fmtDate, fmtTRY, ORDER_TIMELINE } from "../../lib/format";

export default function OrderTrackingPage() {
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/orders/track", { order_number: orderNumber, email });
      setResult(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const currentStep = result ? ORDER_TIMELINE.indexOf(result.status) : -1;
  const isTerminal = result && ["İptal Edildi", "İade Edildi"].includes(result.status);

  return (
    <div className="max-w-xl mx-auto px-4 py-14">
      <Helmet><title>Sipariş Takibi | BIGELDİ</title></Helmet>
      <div className="text-center mb-10">
        <div className="h-14 w-14 rounded-full bg-amber-600/10 text-amber-700 flex items-center justify-center mx-auto mb-4"><Package size={26} /></div>
        <h1 className="font-display font-extrabold text-2xl sm:text-4xl tracking-tight">Sipariş Takibi</h1>
        <p className="text-sm text-neutral-500 mt-2">Sipariş numaranız ve e-posta adresinizle anlık durum sorgulayın.</p>
      </div>

      <form onSubmit={submit} className="bg-white border border-[#E6E4DD] rounded-2xl p-6 space-y-3">
        <input value={orderNumber} onChange={(e) => setOrderNumber(e.target.value.toUpperCase())} required placeholder="Sipariş No (örn. BG-2026-000001)"
          className="w-full h-12 px-4 bg-[#FAF9F5] border border-[#E6E4DD] rounded-xl text-sm font-mono-ed focus:outline-none focus:border-amber-600"
          data-testid="order-tracking-input" />
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="Siparişte kullandığınız e-posta"
          className="w-full h-12 px-4 bg-[#FAF9F5] border border-[#E6E4DD] rounded-xl text-sm focus:outline-none focus:border-amber-600"
          data-testid="order-tracking-email" />
        <button type="submit" disabled={busy}
          className="w-full h-12 rounded-full bg-[#121214] text-white font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="order-tracking-submit">
          <Search size={16} /> {busy ? "Sorgulanıyor..." : "Sorgula"}
        </button>
      </form>

      {result && (
        <div className="bg-white border border-[#E6E4DD] rounded-2xl p-6 mt-6" data-testid="tracking-result">
          <div className="flex justify-between items-center mb-6">
            <div>
              <p className="font-mono-ed font-bold">{result.order_number}</p>
              <p className="text-xs text-neutral-400">{fmtDate(result.created_at)}</p>
            </div>
            <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${isTerminal ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`} data-testid="tracking-status">
              {result.status}
            </span>
          </div>

          {!isTerminal && (
            <div className="flex items-center mb-8">
              {ORDER_TIMELINE.map((step, i) => (
                <div key={step} className="flex items-center flex-1 last:flex-none">
                  <div className="flex flex-col items-center">
                    {i <= currentStep
                      ? <CheckCircle2 size={20} className="text-emerald-500" />
                      : <Circle size={20} className="text-neutral-300" />}
                    <span className={`text-[9px] mt-1.5 text-center w-16 leading-tight ${i <= currentStep ? "text-emerald-700 font-semibold" : "text-neutral-400"}`}>{step}</span>
                  </div>
                  {i < ORDER_TIMELINE.length - 1 && <div className={`h-0.5 flex-1 mx-1 mb-5 ${i < currentStep ? "bg-emerald-500" : "bg-neutral-200"}`} />}
                </div>
              ))}
            </div>
          )}

          {result.cargo_tracking_number && (
            <p className="text-xs bg-[#FAF9F5] rounded-lg p-3 mb-4">
              Kargo: <b>{result.cargo_company || "—"}</b> · Takip No: <span className="font-mono-ed font-bold">{result.cargo_tracking_number}</span>
            </p>
          )}
          {result.shipping_preference && (result.shipping_preference.carrier || result.shipping_preference.delivery_note) && (
            <p className="text-xs bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4" data-testid="tracking-shipping-preference">
              Tercihiniz: <b>{result.shipping_preference.carrier}</b>
              {result.shipping_preference.delivery_note ? ` · ${result.shipping_preference.delivery_note}` : ""}
            </p>
          )}
          <div className="space-y-2 text-sm border-t border-[#E6E4DD] pt-4">
            {result.items.map((i, idx) => (
              <div key={idx} className="flex items-center gap-3">
                {i.image && <img src={i.image} alt="" className="w-10 h-12 rounded-lg object-cover bg-[#F4F2EB]" />}
                <span className="flex-1 text-neutral-600">{i.name} {i.variant_label ? `(${i.variant_label})` : ""} × {i.quantity}</span>
              </div>
            ))}
            <p className="text-right font-mono-ed font-bold pt-2">{fmtTRY(result.total)}</p>
          </div>
        </div>
      )}
    </div>
  );
}
